import React, { useState, useMemo, useRef } from 'react';
import {
  FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, TextField,
  InputAdornment,
} from '@mui/material';
import {
  DescriptionOutlined as ProgramIcon,
  SearchOutlined as SearchIcon,
  InboxOutlined as EmptyIcon,
} from '@mui/icons-material';

export default function ProgramSelect({
  programs    = [],
  value       = '',
  onChange,
  label       = 'Program',
  placeholder = 'Not linked',
  disabled    = false,
  loading     = false,
  size        = 'small',
  fullWidth   = true,
  required    = false,
  error       = false,
  helperText  = null,
  name        = 'program_id',
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return programs;
    const q = search.toLowerCase();
    return programs.filter((p) =>
      p.program_name?.toLowerCase().includes(q)
    );
  }, [programs, search]);

  const handleClose = () => setSearch('');

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      required={required}
      error={error}
    >
      <InputLabel>{loading ? 'Loading programs…' : label}</InputLabel>
      <Select
        name={name}
        value={value}
        label={loading ? 'Loading programs…' : label}
        onChange={(e) => onChange?.(e.target.value, e)}
        onClose={handleClose}
        startAdornment={
          <ProgramIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
        }
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: { maxHeight: 320, borderRadius: 2 },
          },
        }}
        displayEmpty
      >
        {/* ── Search field ── */}
        <Box
          sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search programs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 },
            }}
          />
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* ── Placeholder option ── */}
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">{placeholder}</Typography>
        </MenuItem>

        {/* ── Filtered results ── */}
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" color="text.disabled" display="block">
              No programs match "{search}"
            </Typography>
          </Box>
        ) : (
          filtered.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <Typography variant="body2">{p.program_name}</Typography>
            </MenuItem>
          ))
        )}

        {/* ── Result count ── */}
        {search && filtered.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, borderTop: '0.5px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              {filtered.length} of {programs.length} program{programs.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Select>

      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
}